import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authView, setAuthView] = useState('login');

    useEffect(() => {
        let unsubscribeProfile = null;

        // Listener de Autenticación
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser); // Actualizar usuario inmediatamente

            // 1. Limpiar listener de perfil anterior si existe (evita fugas de memoria y condiciones de carrera)
            if (unsubscribeProfile) {
                unsubscribeProfile();
                unsubscribeProfile = null;
            }

            // 2. Si hay usuario, suscribirse a su perfil en Firestore en tiempo real
            if (currentUser) {
                setShowAuthModal(false); // Cerrar modal si estaba abierto tras login exitoso

                // Opción: No poner setLoading(true) aquí para evitar flasheo si ya tenemos datos cacheados
                // Pero es más seguro para consistencia UI

                const userRef = doc(db, 'users', currentUser.uid);

                unsubscribeProfile = onSnapshot(userRef,
                    (docSnap) => {
                        if (docSnap.exists()) {
                            setUserProfile(docSnap.data());
                        } else {
                            // Usuario autenticado pero sin documento de perfil (raro, pero posible)
                            console.warn("User authenticated but no profile found in Firestore");
                            setUserProfile(null);
                        }
                        setLoading(false); // Carga inicial completada
                    },
                    (error) => {
                        console.error("Error listening to user profile:", error);
                        setLoading(false); // Evitar bloqueo infinito
                    }
                );
            } else {
                // Logout: Limpiar estado
                setUserProfile(null);
                setLoading(false);
            }
        });

        // Cleanup al desmontar el Provider (muy importante)
        return () => {
            if (unsubscribeProfile) unsubscribeProfile();
            unsubscribeAuth();
        };
    }, []);

    const openLogin = () => { setAuthView('login'); setShowAuthModal(true); };
    const openRegister = () => { setAuthView('register'); setShowAuthModal(true); };

    return (
        <AuthContext.Provider value={{
            user,
            userProfile,
            loading,
            showAuthModal,
            setShowAuthModal,
            authView,
            setAuthView,
            openLogin,
            openRegister
        }}>
            {children}
        </AuthContext.Provider>
    );
};
